import { PriceTag } from "@/components/PriceTag";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getProduct, listReviews, postReview, trackPageView, trackView } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/LanguageContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { useSEO, useJsonLd } from "@/hooks/useSEO";
import { TESTIDS } from "@/constants/testIds";
import WaitlistBlock from "@/components/WaitlistBlock";
import RelatedProducts from "@/components/RelatedProducts";
import ProgressiveImage from "@/components/ProgressiveImage";
import StockBadge from "@/components/StockBadge";
import { cldImage, cldSrcSet, imgPos } from "@/lib/utils";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";

const Stars = ({ value, size = 14, onChange, testIdPrefix }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type={onChange ? "button" : undefined}
        data-testid={testIdPrefix ? `${testIdPrefix}-${n}` : undefined}
        onClick={onChange ? () => onChange(n) : undefined}
        disabled={!onChange}
        className={n <= value ? "star-filled" : "star-empty"}
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
      >
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
      </button>
    ))}
  </div>
);

const PDP = ({ onRequireAuth }) => {
  const { t } = useT();
  const { settings } = useSiteContent();
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeMedia, setActiveMedia] = useState(0);
  // Lightbox: null = closed, otherwise the index (in the imagesOnly list)
  // to open at. Videos are excluded — pinch-to-zoom is only meaningful
  // for stills.
  const [zoomIndex, setZoomIndex] = useState(null);
  const [addedFlash, setAddedFlash] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  const galleryRef = useRef(null);
  const cart = useCart();
  const { user } = useAuth();

  // Product-specific SEO (overrides the App-level default)
  useSEO({
    title: product?.name,
    description: product?.short_description || product?.description?.slice(0, 155),
    image: product?.images?.[0],
    ogType: "product",
  });

  // Rich product structured data — helps Google show the item in Shopping / rich results
  const productJsonLd = useMemo(() => {
    if (!product) return null;
    const avg = reviews.length
      ? Number((reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1))
      : null;
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "image": product.images,
      "description": product.short_description || product.description,
      "sku": product.sku,
      "brand": { "@type": "Brand", "name": "GOYA LISBOA" },
      "offers": {
        "@type": "Offer",
        "price": product.price,
        "priceCurrency": product.currency || "EUR",
        "availability": "https://schema.org/InStock",
        "url": typeof window !== "undefined" ? window.location.href : undefined,
      },
      ...(avg && reviews.length
        ? {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": avg,
              "reviewCount": reviews.length,
            },
          }
        : {}),
    };
  }, [product, reviews]);
  useJsonLd("pdp-product-jsonld", productJsonLd);

  useEffect(() => {
    let mounted = true;
    setProduct(null); setSelectedVariant(null); setActiveMedia(0); setQty(1);
    getProduct(slug).then((p) => {
      if (!mounted) return;
      setProduct(p);
      setSelectedVariant(p.variants?.[0] || null);
    });
    listReviews(slug).then((r) => mounted && setReviews(r)).catch(() => {});
    trackView(slug).catch(() => {});
    return () => { mounted = false; };
  }, [slug]);

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center font-typewriter text-xs tracking-[0.22em] text-[#6B6862]">{t("collection.developing")}</div>;
  }

  const media = [
    ...(product.videos || []).map((url) => ({ type: "video", url })),
    ...product.images.map((url) => ({ type: "image", url })),
  ];

  // Images-only list for the zoom lightbox. Videos are dropped and we
  // remember each image's index in the original `media` array so
  // clicking the third slide (say media[3]) opens the right frame.
  const imageSlides = media
    .map((m, idx) => ({ ...m, mediaIdx: idx }))
    .filter((m) => m.type === "image");
  const findImageIndex = (mediaIdx) =>
    imageSlides.findIndex((s) => s.mediaIdx === mediaIdx);

  const onScrollGallery = () => {
    const el = galleryRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    setActiveMedia(i);
  };

  const handleAdd = () => {
    // Defence-in-depth — the ATC button is already disabled when OOS, but
    // Cypress / keyboard users might still trigger onClick. Bail early.
    if (!selectedVariant || (selectedVariant.stock ?? 0) <= 0) return;
    cart.addItem({
      productId: product.id,
      slug: product.slug,
      name: product.name,
      image: product.images[0],
      price: product.price,
      originalPrice: product.original_price,
      variantName: selectedVariant?.name || "",
      quantity: qty,
      stock: selectedVariant.stock,
    });
    // Fire-and-forget popularity event so the admin dashboard can
    // rank products by add-to-cart interest. Never blocks the UI.
    try {
      const sess = sessionStorage.getItem("gl_visitor_session") || "";
      trackPageView({
        path: `/product/${product.slug}`,
        event: "add_to_cart",
        product_slug: product.slug,
        session_id: sess,
      });
    } catch (_e) { /* analytics failures never block a purchase */ }
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 1400);
  };

  const handleAddBundle = (lookProducts) => {
    const items = [
      {
        productId: product.id,
        slug: product.slug,
        name: product.name,
        image: product.images[0],
        price: product.price,
        originalPrice: product.original_price,
        variantName: selectedVariant?.name || "",
      },
      ...lookProducts.map((lp) => ({
        productId: lp.id,
        slug: lp.slug,
        name: lp.name,
        image: lp.images[0],
        price: lp.price,
        originalPrice: lp.original_price,
        variantName: lp.variants?.[0]?.name || "",
      })),
    ];
    cart.addBundle(`look-of-${product.slug}`, items);
    // Track the bundle add too — one event per product in the look so
    // "Total look" merchandising surfaces in the popularity list.
    try {
      const sess = sessionStorage.getItem("gl_visitor_session") || "";
      [product, ...lookProducts].forEach((p) => {
        trackPageView({
          path: `/product/${p.slug}`,
          event: "add_to_cart",
          product_slug: p.slug,
          session_id: sess,
        });
      });
    } catch (_e) { /* analytics failures never block a purchase */ }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { onRequireAuth?.(); return; }
    const form = e.target;
    const fd = new FormData(form);
    const photo_urls = (fd.get("photo_urls") || "").toString().split(/[,\s]+/).filter(Boolean);
    const payload = {
      rating: Number(fd.get("rating")) || 5,
      title: (fd.get("title") || "").toString(),
      body: (fd.get("body") || "").toString(),
      photo_urls,
    };
    try {
      const r = await postReview(slug, payload);
      setReviews([r, ...reviews]);
      setShowReviewForm(false);
      form.reset();
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not post review.");
    }
  };

  return (
    <div className="pt-24 md:pt-32 pb-32 md:pb-16">
      {/* ── Top section: gallery (mobile = scroll snap, desktop = sticky vertical) ─ */}
      <div className="grid md:grid-cols-12 md:gap-12 md:px-12">

        {/* GALLERY */}
        <div className="md:col-span-6 md:col-start-2 relative md:max-w-[560px]">
          <div
            ref={galleryRef}
            data-testid={TESTIDS.pdpGallery}
            onScroll={onScrollGallery}
            className="flex overflow-x-auto snap-x-mandatory no-scrollbar md:grid md:grid-cols-2 md:gap-3 md:overflow-visible md:snap-none"
          >
            {media.map((m, i) => (
              <div
                key={i}
                data-testid={TESTIDS.pdpGalleryItem(i)}
                className={`snap-start shrink-0 w-full md:w-auto relative bg-[#FFFFFF] ${
                  i === 0 ? "md:col-span-2 md:mb-2" : ""
                }`}
                style={{ aspectRatio: "3 / 4" }}
              >
                {i === 0 && (product.trending || product.back_in_stock) && (
                  <div className="absolute top-4 left-4 z-10 flex flex-col items-start gap-1.5">
                    {product.trending && (
                      <div data-testid={TESTIDS.pdpTrendingBadge} className="badge-trending">{t("pdp.trending")}</div>
                    )}
                    {product.back_in_stock && (
                      <div
                        data-testid="pdp-back-in-stock-badge"
                        className="bg-[#241C18] text-[var(--gl-orange)] font-badge text-[10px] tracking-[0.14em] uppercase px-3 py-1 rounded-full"
                      >{t("badge.backInStock")}</div>
                    )}
                  </div>
                )}
                {m.type === "video" ? (
                  <>
                    <video
                      src={m.url}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    <div className="video-tag">{t("pdp.silentLoop")}</div>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setZoomIndex(findImageIndex(i))}
                    className="absolute inset-0 w-full h-full block cursor-zoom-in"
                    aria-label={t("pdp.zoom") || "Zoom"}
                    data-testid={`pdp-zoom-trigger-${i}`}
                  >
                    <ProgressiveImage
                      src={cldImage(m.url, i === 0 ? 600 : 400)}
                      srcSet={cldSrcSet(m.url, [400, 600, 800])}
                      sizes={i === 0 ? "(max-width: 768px) 100vw, 560px" : "(max-width: 768px) 100vw, 280px"}
                      alt={`${product.name} ${i}`}
                      priority={i === 0}
                      position={imgPos(product, m.url)}
                      className="w-full h-full object-cover"
                    />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* mobile dots */}
          <div className="md:hidden flex justify-center gap-1.5 mt-4">
            {media.map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === activeMedia ? "w-6 bg-[#2C2C2C]" : "w-1.5 bg-[#E3DFD7]"}`} />
            ))}
          </div>
        </div>

        {/* DETAILS */}
        <div className="md:col-span-5 px-6 md:px-0 mt-10 md:mt-0 md:sticky md:top-32 md:self-start">
          <div className="font-typewriter text-[10px] tracking-[0.32em] uppercase text-[#6B6862]" data-testid={TESTIDS.pdpSku}>
            {product.sku} · {product.category}
          </div>
          <h1 data-testid={TESTIDS.pdpName} className="font-serif-display text-4xl md:text-5xl mt-3 leading-[1.05] tracking-tight">{product.name}</h1>
          {product.short_description?.trim() && (
            <div className="mt-2 italic text-[#6B6862] font-serif-display">"{product.short_description}"</div>
          )}
          <div className="mt-5 font-serif-display text-2xl"><PriceTag product={product} testid={TESTIDS.pdpPrice} /></div>
          <div className="mt-3">
            <StockBadge product={product} size="md" />
          </div>

          <p className="mt-8 text-sm font-sans-clean leading-relaxed text-[#2C2C2C]">
            {product.description}
          </p>

          {/* Variant chooser — hidden for one-of-a-kind pieces (single variant, no size/colour) */}
          {product.variants?.length > 0 && !(product.variants.length === 1 && !product.variants[0].color && !product.variants[0].size) && (() => {
            // Compute total in-stock across variants once — when ≤ 3 we
            // append the exact count inline on each in-stock chip so
            // shoppers see "M / 38 · 1 left" and immediately know what's
            // running out. Silent for well-stocked pieces so the chip
            // grid stays clean.
            const inStock = (product.variants || []).filter((v) => (Number(v.stock) || 0) > 0);
            const totalInStock = inStock.reduce((sum, v) => sum + Number(v.stock || 0), 0);
            const threshold = Math.max(1, Number(settings?.stock_left_threshold) || 3);
            const showCounts = totalInStock > 0
              && totalInStock <= threshold
              && settings?.show_stock_left !== false
              && product?.show_scarcity !== false;
            return (
            <div className="mt-8">
              <div className="font-typewriter text-[10px] tracking-[0.3em] uppercase text-[#6B6862] mb-3">{t("pdp.colourSize")}</div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => {
                  const active = selectedVariant?.name === v.name;
                  const oos = v.stock <= 0;
                  return (
                    <button
                      key={v.name}
                      data-testid={TESTIDS.pdpVariantChip(v.name)}
                      onClick={() => !oos && setSelectedVariant(v)}
                      disabled={oos}
                      className={`chip ${active ? "chip-active" : ""} ${oos ? "chip-oos" : ""}`}
                    >
                      {v.name}
                      {showCounts && !oos && (
                        <span
                          data-testid={`pdp-variant-stock-${v.name}`}
                          className={`ml-2 font-sans-clean text-[10px] tracking-normal ${active ? "text-[var(--gl-orange)]/80" : "text-[#8B1A1A]"}`}
                        >
                          · {v.stock} {t("pdp.stockLeft")}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {selectedVariant && selectedVariant.model_wearing && product.show_model_wearing !== false && (
                <div data-testid={TESTIDS.pdpModelWearing} className="mt-5 border-l-2 border-[#C97A63] pl-4 italic font-serif-display text-[#6B6862]">
                  {t("pdp.modelWearing")} <span className="text-[#2C2C2C]">{selectedVariant.model_wearing}</span>
                </div>
              )}

              {selectedVariant && selectedVariant.stock <= 0 && (
                <WaitlistBlock productSlug={product.slug} variantName={selectedVariant.name} />
              )}
            </div>
            );
          })()}

          {/* One-of-a-kind stock notice / waitlist */}
          {product.variants?.length === 1 && !product.variants[0].color && !product.variants[0].size && selectedVariant && selectedVariant.stock <= 0 && (
            <div className="mt-6">
              <WaitlistBlock productSlug={product.slug} variantName={selectedVariant.name} />
            </div>
          )}

          {/* Qty + Add to cart (desktop) */}
          <div className="mt-8 hidden md:flex items-center gap-4">
            <div className="stepper">
              <button data-testid={TESTIDS.pdpQtyMinus} onClick={() => setQty(Math.max(1, qty - 1))}>−</button>
              <span>{qty}</span>
              <button
                data-testid={TESTIDS.pdpQtyPlus}
                onClick={() => setQty(Math.min((selectedVariant?.stock ?? 99), qty + 1))}
                disabled={!!selectedVariant && qty >= (selectedVariant.stock ?? Infinity)}
              >+</button>
            </div>
            <button
              data-testid={TESTIDS.pdpAddToCart}
              onClick={handleAdd}
              disabled={!selectedVariant || (selectedVariant.stock ?? 0) <= 0}
              className="btn-ink flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(!selectedVariant || (selectedVariant.stock ?? 0) <= 0)
                ? t("pdp.soldOut")
                : addedFlash ? t("pdp.added") : `${t("pdp.addToBag")} · €${(product.price * qty).toFixed(2)}`}
            </button>
          </div>

          {/* details accordion lite */}
          <div className="mt-10 border-t border-[#E3DFD7] divide-y divide-[#E3DFD7]">
            {[
              { t: t("pdp.acc.fabric"), v: (product.fabric_care && product.fabric_care.trim()) || t("pdp.acc.fabric.body") },
              { t: t("pdp.acc.shipping"), v: (product.shipping_info && product.shipping_info.trim()) || t("pdp.acc.shipping.body") },
              { t: t("pdp.acc.returns"), v: (product.returns_info && product.returns_info.trim()) || t("pdp.acc.returns.body") },
            ].map((row) => (
              <details key={row.t} className="py-4">
                <summary className="cursor-pointer flex justify-between items-center font-typewriter text-[11px] tracking-[0.24em] uppercase text-[#2C2C2C]">
                  {row.t}<span className="text-base">＋</span>
                </summary>
                <p className="mt-3 text-sm text-[#6B6862] font-sans-clean leading-relaxed whitespace-pre-wrap">{row.v}</p>
              </details>
            ))}
          </div>
        </div>
      </div>

      {/* ── SHOP THE LOOK ───────────────────────────────────────── */}
      {product.shop_the_look?.length > 0 && (
        <section data-testid={TESTIDS.pdpShopTheLook} className="mt-24 px-6 md:px-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="font-typewriter text-[10px] tracking-[0.3em] uppercase text-[#6B6862]">{t("pdp.shopTheLook.eyebrow")}</div>
              <h2 className="font-serif-display text-3xl md:text-5xl mt-2 tracking-tight">{t("pdp.shopTheLook.title")}</h2>
              <div className="mt-2 text-sm font-sans-clean italic text-[#C97A63]">{t("pdp.shopTheLook.subtitle")}</div>
            </div>
            <button
              data-testid={TESTIDS.pdpShopLookAdd}
              onClick={() => handleAddBundle(product.shop_the_look)}
              className="btn-ink hidden md:inline-flex"
            >{t("pdp.shopTheLook.add")}</button>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-4 gap-x-3 gap-y-10 md:gap-x-8 md:gap-y-14 items-start">
            <div className="group">
              <div className="relative aspect-[3/4] overflow-hidden bg-[var(--gl-bg-alt)]">
                <div className="absolute top-2 left-2 z-10 font-badge text-[9px] tracking-[0.22em] uppercase text-white bg-[#241C18] px-2 py-0.5 rounded-full">{t("pdp.shopTheLook.thisPiece")}</div>
                <ProgressiveImage src={product.images[0]} alt={product.name} position={imgPos(product, product.images[0])} className="w-full h-full object-cover" />
              </div>
              <div className="mt-3">
                <div className="font-serif-display text-sm md:text-base leading-tight truncate text-[#241C18]">{product.name}</div>
                <PriceTag product={product} showPercent={false} className="font-badge text-[10px] text-[var(--gl-muted)] mt-1" />
              </div>
            </div>
            {product.shop_the_look.map((lp) => (
              <Link key={lp.id} to={`/product/${lp.slug}`} className="group block">
                <div className="swap-wrap aspect-[3/4] bg-[var(--gl-bg-alt)] overflow-hidden">
                  <ProgressiveImage src={lp.images[0]} alt={lp.name} position={imgPos(lp, lp.images[0])} className="swap-front w-full h-full object-cover" />
                  {lp.images[1] && <img src={lp.images[1]} alt="" loading="lazy" decoding="async" style={{ objectPosition: imgPos(lp, lp.images[1]) }} className="swap-back" />}
                </div>
                <div className="mt-3">
                  <div className="font-serif-display text-sm md:text-base leading-tight truncate text-[#241C18]">{lp.name}</div>
                  <PriceTag product={lp} showPercent={false} className="font-badge text-[10px] text-[var(--gl-muted)] mt-1" />
                </div>
              </Link>
            ))}
          </div>

          <button
            onClick={() => handleAddBundle(product.shop_the_look)}
            className="btn-ink w-full mt-6 md:hidden"
          >{t("pdp.shopTheLook.addAll")}</button>
        </section>
      )}

      {/* ── REVIEWS ──────────────────────────────────────────── */}
      {reviews.length > 0 && (
        <section data-testid={TESTIDS.pdpReviewsSection} className="mt-32 px-6 md:px-12">
          <div className="rule-center mb-10">{t("pdp.reviews.eyebrow")}</div>

          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight">{t("pdp.reviews.title")}</h2>
              <div className="mt-2 text-sm text-[#6B6862] italic">{t("pdp.reviews.count", { n: reviews.length, letters: reviews.length === 1 ? t("pdp.reviews.letter") : t("pdp.reviews.letters") })}</div>
            </div>
            <button onClick={() => user ? setShowReviewForm((s) => !s) : onRequireAuth?.()} className="btn-ghost">
              {showReviewForm ? t("pdp.reviews.close") : t("pdp.reviews.write")}
            </button>
          </div>

          {showReviewForm && user && (
            <form data-testid={TESTIDS.pdpReviewForm} onSubmit={submitReview} className="bg-[#FFFFFF] p-6 md:p-10 border border-[#E3DFD7] mb-12 grid md:grid-cols-2 gap-x-8 gap-y-5">
              <div className="md:col-span-2 flex items-center gap-4">
                <div className="font-typewriter text-[10px] tracking-[0.3em] uppercase text-[#6B6862]">{t("pdp.reviews.stars")}</div>
                <input type="hidden" name="rating" id="rating" defaultValue={5} />
                <Stars value={5} size={20} onChange={(n) => (document.getElementById("rating").value = n)} testIdPrefix={TESTIDS.pdpReviewRating("").replace(/-$/, "")} />
              </div>
              <input data-testid={TESTIDS.pdpReviewTitle} name="title" required placeholder={t("pdp.reviews.titlePh")} className="input-editorial md:col-span-2" />
              <textarea data-testid={TESTIDS.pdpReviewBody} name="body" required placeholder={t("pdp.reviews.bodyPh")} rows={4} className="input-editorial md:col-span-2 resize-none" />
              <input data-testid={TESTIDS.pdpReviewPhotos} name="photo_urls" placeholder={t("pdp.reviews.photosPh")} className="input-editorial md:col-span-2" />
              <button data-testid={TESTIDS.pdpReviewSubmit} type="submit" className="btn-ink md:col-span-2">{t("pdp.reviews.submit")}</button>
            </form>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            {reviews.map((r) => (
              <article key={r.id} className="bg-[#FFFFFF] p-5 md:p-6 border border-[#E3DFD7]">
                {r.photo_urls?.[0] && (
                  <div className="aspect-square mb-4 overflow-hidden bg-[#F9F8F5]">
                    <img
                      src={r.photo_urls[0]}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                    />
                  </div>
                )}
                <Stars value={r.rating} />
                <div className="font-serif-display text-lg mt-3 leading-tight">"{r.title}"</div>
                <p className="text-sm text-[#6B6862] mt-2 leading-relaxed font-sans-clean">{r.body}</p>
                <div className="font-typewriter text-[10px] tracking-[0.22em] uppercase text-[#6B6862] mt-4">— {r.user_name}</div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* ── RELATED PRODUCTS ──────────────────────────────────── */}
      <RelatedProducts slug={product.slug} />

      {/* ── STICKY MOBILE ATC ─────────────────────────────────── */}
      <div data-testid={TESTIDS.pdpStickyAtc} className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#F9F8F5]/96 backdrop-blur-xl border-t border-[#E3DFD7] sticky-atc-shadow px-4 py-3 flex items-center gap-3">
        <div className="flex-1">
          <div className="font-typewriter text-[9px] tracking-[0.22em] uppercase text-[#6B6862] truncate">
            {(() => {
              const isOneOfKind = product.variants?.length === 1 && !product.variants[0].color && !product.variants[0].size;
              if (isOneOfKind) return product.name;
              return selectedVariant?.name || t("pdp.selectVariant");
            })()}
          </div>
          <div className="font-serif-display text-lg leading-none">€{(product.price * qty).toFixed(2)}</div>
        </div>
        <div className="stepper">
          <button onClick={() => setQty(Math.max(1, qty - 1))}>−</button>
          <span>{qty}</span>
          <button
            onClick={() => setQty(Math.min((selectedVariant?.stock ?? 99), qty + 1))}
            disabled={!!selectedVariant && qty >= (selectedVariant.stock ?? Infinity)}
          >+</button>
        </div>
        <button
          onClick={handleAdd}
          disabled={!selectedVariant || (selectedVariant.stock ?? 0) <= 0}
          className="btn-ink !px-4 !py-3 !text-[0.65rem] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {(!selectedVariant || (selectedVariant.stock ?? 0) <= 0)
            ? t("pdp.soldOut")
            : addedFlash ? t("pdp.added") : t("pdp.addToBag")}
        </button>
      </div>

      {/* ── ZOOM LIGHTBOX ─────────────────────────────────────────
          Pinch-to-zoom on mobile, wheel + double-click zoom on desktop.
          Only image slides — videos aren't zoomable anyway. */}
      <Lightbox
        open={zoomIndex !== null}
        close={() => setZoomIndex(null)}
        index={zoomIndex ?? 0}
        slides={imageSlides.map((s) => ({ src: s.url, alt: product.name }))}
        plugins={[Zoom, Counter]}
        zoom={{ maxZoomPixelRatio: 3, doubleTapDelay: 300, doubleClickDelay: 300, scrollToZoom: true }}
        controller={{ closeOnBackdropClick: true }}
        // Warm, boutique-style backdrop matching the site palette
        // (Lady Pipa / Elephant Rooms both use light neutrals for their
        // zoom overlays rather than a jarring black).
        className="gl-lightbox"
        styles={{
          container: { backgroundColor: "rgba(255, 243, 230, 0.98)" },
        }}
      />
    </div>
  );
};

export default PDP;
